import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingCancelPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Cancel booking — BrandChilly";
    if (!token) {
      setError("Missing cancellation token.");
      setLoading(false);
      return;
    }
    axios.get(`${API}/booking/cancel/${token}`)
      .then((r) => setBooking(r.data))
      .catch(() => setError("This cancellation link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/booking/cancel/${token}`);
      setCancelled(true);
    } catch {
      setError("Could not cancel. Maybe it was already cancelled.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="coaches-page" style={{ minHeight: "100vh", background: "var(--c-bg)" }}>
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-14 md:h-16 flex items-center">
          <a href="/"><img src="/assets/logo.png" alt="BrandChilly" className="h-8 md:h-10 w-auto" /></a>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        {loading ? (
          <div className="c-muted">Loading…</div>
        ) : error ? (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 border border-red-700/40 c-red mb-6" style={{ borderRadius: 0 }}>
              <AlertCircle size={24} />
            </div>
            <h1 className="text-3xl md:text-4xl">{error}</h1>
            <a href="/" className="c-btn-primary mt-8 inline-flex">Back to home</a>
          </>
        ) : cancelled ? (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 border border-red-600 c-red mb-6" style={{ borderRadius: 0 }}>
              <Check size={24} />
            </div>
            <h1 className="text-3xl md:text-4xl">Booking cancelled.</h1>
            <p className="mt-4 c-muted text-sm">The slot has been freed up for others. Sorry to miss you.</p>
            <a href="/book" className="c-btn-primary mt-8 inline-flex">Book another slot</a>
          </>
        ) : (
          <>
            <div className="c-eyebrow mb-3">Cancel booking</div>
            <h1 className="text-3xl md:text-4xl leading-[1.1]">Are you sure you want to cancel?</h1>
            {booking && (
              <div className="border border-white/10 px-5 py-4 inline-block mt-6" style={{ borderRadius: 0 }}>
                <div className="text-xs c-muted tracking-[0.18em] uppercase mb-1">Booked slot</div>
                <div className="text-xl text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>{booking.date}</div>
                <div className="text-base c-red mt-1">{booking.time} IST</div>
              </div>
            )}
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <button onClick={doCancel} disabled={cancelling} className="c-btn-primary disabled:opacity-60" data-testid="confirm-cancel-btn">
                <X size={16} /> {cancelling ? "Cancelling…" : "Yes, cancel my booking"}
              </button>
              <a href="/" className="text-sm c-muted hover:text-white inline-flex items-center justify-center px-5 py-3 border border-white/10" style={{ borderRadius: 0 }}>
                Keep my booking
              </a>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
